import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileSignature, IdCard, CreditCard, Printer, Loader2, Clock, AlertTriangle, Ban } from "lucide-react";
import { PriceBreakdown, PaymentSchedule, money } from "@/components/PriceBreakdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const card = "rounded-2xl border border-[#E7E5E4] bg-white p-5 md:p-6";
const btn = "inline-flex items-center justify-center gap-2 rounded-full bg-[#1C1917] text-white px-6 py-3 hover:bg-[#44403C] transition-colors duration-300 font-medium text-sm disabled:opacity-60";
const inp = "w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] h-11 px-3 text-[#1C1917] text-sm";
const eyebrow = "text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]";

export default function Pay() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [transfer, setTransfer] = useState(null);
  const [error, setError] = useState("");
  const returnedFromStripe = params.get("paid") === "1" || params.get("transfer_paid") === "1";

  const load = useCallback(async () => {
    try {
      if (returnedFromStripe) await axios.post(`${API}/pay/${id}/refresh`).catch(() => null);
      const { data: d } = await axios.get(`${API}/pay/${id}`);
      setData(d);
      const { data: t } = await axios.get(`${API}/pay/${id}/transfer`).catch(() => ({ data: { pending: false } }));
      setTransfer(t.pending ? t.transfer : null);
    } catch (e) {
      setError(e.response?.status === 404 ? "We couldn't find this reservation." : "Unable to load your reservation right now.");
    }
  }, [id, returnedFromStripe]);
  useEffect(() => { load(); }, [load]);

  const paidCount = (data?.ledger?.schedule || []).filter((s) => s.status === "paid").length;
  useEffect(() => {
    if (!returnedFromStripe) return undefined;
    let n = 0;
    const t = setInterval(() => { n += 1; if (n > 6) clearInterval(t); else load(); }, 3000);
    return () => clearInterval(t);
  }, [returnedFromStripe, paidCount, load]);

  if (error) return <Shell><p data-testid="pay-error" className="text-[#44403C]">{error}</p></Shell>;
  if (!data) return <Shell><Loader2 className="w-5 h-5 animate-spin text-[#8A9A86]" /></Shell>;

  const r = data.reservation || {};
  const led = data.ledger || {};
  const paid = (led.payments || []).filter((p) => p.status === "paid");
  const next = led.next_installment;
  const fullyPaid = !next;

  return (
    <Shell>
      {returnedFromStripe && (
        <Banner testid="pay-processing" icon={Clock}>Thank you. Your payment is being confirmed; this page updates automatically.</Banner>
      )}
      {params.get("cancelled") === "1" && (
        <Banner testid="pay-cancelled" icon={Clock}>Checkout was cancelled. You can complete payment whenever you're ready.</Banner>
      )}
      {r.payment_overdue && r.status !== "Confirmed" && (
        <Banner testid="pay-overdue" icon={AlertTriangle}>Your payment is past due. Your reservation and dates are still held — please complete payment to confirm, or contact us with any questions.</Banner>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <Summary r={r} bd={data.breakdown} taxStatus={data.tax_status} />
          <div className={card} data-testid="pay-invoice">
            <div className="flex items-center justify-between gap-3 mb-4">
              <p className={eyebrow}>{paid.length ? "Invoice & receipts" : "Invoice"}</p>
              <button onClick={() => window.print()} data-testid="pay-print" className="print:hidden inline-flex items-center gap-1.5 text-xs text-[#44403C] hover:text-[#1C1917]">
                <Printer className="w-3.5 h-3.5" /> Print
              </button>
            </div>
            <PriceBreakdown bd={data.breakdown} testidPrefix="pay" showDue={false} />
            <div className="mt-4 rounded-lg bg-[#F5F5F4] p-3 text-sm space-y-1">
              <Row label="Paid to date" value={money(led.amount_paid)} testid="pay-paid-to-date" />
              <Row label="Outstanding balance" value={money(Math.max(0, led.outstanding_balance ?? 0))} testid="pay-outstanding" />
              <Row label="Payment status" value={led.payment_status || "Unpaid"} testid="pay-status" />
              {next && <Row label={`Next payment · ${next.due_label}`} value={money(next.amount)} testid="pay-next-due" />}
            </div>
            <p className={eyebrow + " mt-6 mb-1"}>Payment schedule</p>
            <PaymentSchedule rows={led.schedule} testidPrefix="pay-schedule" />
            {paid.map((p) => (
              <p key={p.id} data-testid={`pay-receipt-${p.installment}`} className="mt-2 text-xs text-[#78716C]">
                Receipt · installment {p.installment} · {money(p.amount)} paid {new Date(p.paid_at || p.created_at).toLocaleDateString()} · Ref {(p.stripe_payment_intent || p.stripe_session_id || "").slice(-10)}
              </p>
            ))}
          </div>
        </div>
        <div className="lg:col-span-2 space-y-6 print:hidden">
          {transfer && <TransferStep id={id} transfer={transfer} onDone={load} />}
          {r.status === "Confirmed" && (
            <div className={card} data-testid="pay-confirmed">
              <CheckCircle2 className="w-8 h-8 text-[#8A9A86]" strokeWidth={1.5} />
              <p className="mt-3 text-lg text-[#1C1917]">Reservation confirmed</p>
              <p className="mt-1 text-sm text-[#44403C] font-light">
                {fullyPaid ? "Your reservation is paid in full." : "Future monthly payments will be requested by secure link on each due date. We never charge your card without your authorization."}
              </p>
            </div>
          )}
          <AgreementStep id={id} agreement={data.agreement} preview={data.agreement_preview} revised={data.revised_total_required} onDone={load} />
          {data.revised_total_required && data.revised_agreement_preview && (
            <RevisedTotalStep id={id} bd={data.breakdown} preview={data.revised_agreement_preview} onDone={load} />
          )}
          {data.revised_total_acceptance && !data.revised_total_required && (
            <p data-testid="revised-total-accepted" className="text-xs text-[#4A6A48]">
              Revised total {money(data.revised_total_acceptance.grand_total)} accepted by {data.revised_total_acceptance.signed_full_name} on {new Date(data.revised_total_acceptance.accepted_at).toLocaleString()}.
            </p>
          )}
          <IdStep id={id} />
          {!fullyPaid && <PayStep next={next} signed={Boolean(data.agreement)} taxStatus={data.tax_status} revised={data.revised_total_required} deadline={r.payment_deadline} notice={data.payments_enabled ? "" : data.payments_notice} />}
          {!["Declined", "Cancelled"].includes(r.status) && (
            <CancellationStep id={id} existing={data.cancellation_request} onDone={load} />
          )}
        </div>
      </div>
    </Shell>
  );
}

function Row({ label, value, testid }) {
  return <div className="flex justify-between gap-3"><span className="text-[#44403C]">{label}</span><span data-testid={testid} className="text-[#1C1917]">{value}</span></div>;
}

function CancellationStep({ id, existing, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/pay/${id}/request-cancellation`, { reason });
      toast.success("Your cancellation request was sent to our team for review.");
      setOpen(false);
      await onDone();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not send your request. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  if (existing) {
    return (
      <div className={card} data-testid="pay-cancellation-pending">
        <div className="flex items-center gap-2 text-[#8A5A2B]"><Clock className="w-4 h-4" strokeWidth={1.75} /><p className="text-sm font-medium">Cancellation requested</p></div>
        <p className="mt-2 text-sm text-[#44403C] font-light">
          We received your request{existing.requested_at ? ` on ${new Date(existing.requested_at).toLocaleDateString()}` : ""} and our team is reviewing it. Your reservation stays active until we confirm. We'll be in touch shortly.
        </p>
      </div>
    );
  }
  return (
    <div className={card} data-testid="pay-cancellation-card">
      <div className="flex items-center gap-2"><Ban className="w-4 h-4 text-[#78716C]" strokeWidth={1.75} /><p className={eyebrow}>Need to cancel?</p></div>
      {!open ? (
        <>
          <p className="mt-2 text-sm text-[#44403C] font-light">You can request a cancellation and our team will review it. Submitting a request does not cancel your reservation automatically.</p>
          <button onClick={() => setOpen(true)} data-testid="pay-request-cancellation-open" className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#E7E5E4] px-5 py-2.5 text-sm text-[#44403C] hover:bg-[#F5F5F4] transition-colors duration-300">Request cancellation</button>
        </>
      ) : (
        <div className="mt-3 space-y-3">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pay-cancellation-reason" rows={3} className="w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] px-3 py-2 text-[#1C1917] text-sm" placeholder="Optionally, let us know why (helps us assist you faster)" />
          <div className="flex items-center gap-2">
            <button onClick={submit} disabled={busy} data-testid="pay-request-cancellation-submit" className={btn + " text-sm"}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Send request</button>
            <button onClick={() => setOpen(false)} disabled={busy} data-testid="pay-request-cancellation-cancel" className="text-sm text-[#78716C] hover:text-[#44403C]">Never mind</button>
          </div>
        </div>
      )}
    </div>
  );
}

function TransferStep({ id, transfer, onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const accepted = Boolean(transfer.addendum?.accepted_at);
  const needsPay = Number(transfer.upgrade_due_now) > 0 && !transfer.upgrade_paid;
  const accept = async () => {
    if (name.trim().length < 2) { toast.error("Please type your full name to accept."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/pay/${id}/transfer/${transfer.id}/accept-addendum`, { full_name: name });
      toast.success("Transfer addendum accepted");
      onDone();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not record your acceptance.");
    } finally { setBusy(false); }
  };
  return (
    <div className={card + " border-[#8A9A86]"} data-testid="transfer-step">
      <p className={eyebrow}>Room change requested</p>
      <p className="mt-2 text-lg text-[#1C1917]">Move to {transfer.to_room?.name}</p>
      <p className="mt-1 text-sm text-[#44403C] font-light">Effective {transfer.transfer_date} · {transfer.affected_nights} affected night(s). Your booking reference, ID, signed agreement and prior payments are preserved.</p>
      <div className="mt-3 rounded-lg bg-[#F5F5F4] p-3 text-sm space-y-1">
        {transfer.complimentary ? (
          <Row label="Upgrade" value="Complimentary — waived" testid="transfer-complimentary" />
        ) : (
          <Row label="Upgrade due now" value={money(transfer.upgrade_due_now)} testid="transfer-upgrade-due" />
        )}
        {transfer.credit_amount > 0 && <Row label="Credit (under review)" value={money(transfer.credit_amount)} testid="transfer-credit" />}
        <Row label={transfer.complimentary ? "Total (unchanged)" : "Revised total"} value={money(transfer.revised_grand_total)} testid="transfer-revised-total" />
      </div>
      {transfer.complimentary && <p className="mt-2 text-xs text-[#4A6A48]" data-testid="transfer-complimentary-note">This is a complimentary room change at no extra cost. Your original total and payment schedule stay the same — just accept the addendum below.</p>}
      <details className="mt-3 text-xs text-[#78716C]">
        <summary className="cursor-pointer text-[#1C1917]">Read the transfer addendum</summary>
        <pre className="mt-2 whitespace-pre-wrap font-sans">{transfer.addendum?.text}</pre>
      </details>
      {accepted ? (
        <p className="mt-3 text-xs text-[#4A6A48]" data-testid="transfer-accepted">Addendum accepted by {transfer.addendum.accepted_by}. Thank you.</p>
      ) : (
        <div className="mt-3 space-y-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full name to accept" data-testid="transfer-name" className={inp} />
          <button disabled={busy} onClick={accept} data-testid="transfer-accept-btn" className={btn + " w-full"}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Accept transfer addendum
          </button>
        </div>
      )}
      {accepted && needsPay && transfer.upgrade_checkout_url && (
        <a href={transfer.upgrade_checkout_url} data-testid="transfer-pay-btn" className={btn + " w-full mt-3"}>
          <CreditCard className="w-4 h-4" /> Pay upgrade {money(transfer.upgrade_due_now)}
        </a>
      )}
      {accepted && needsPay && !transfer.upgrade_checkout_url && (
        <p className="mt-3 text-xs text-[#78716C]" data-testid="transfer-pay-pending">Your host is preparing your secure payment link for the upgrade. You'll be able to pay here shortly.</p>
      )}
      {accepted && !needsPay && (
        <p className="mt-3 text-xs text-[#78716C]">No further action needed — your host will confirm the move.</p>
      )}
    </div>
  );
}

function Shell({ children }) {
  return (
    <section data-testid="pay-page" className="px-6 md:px-12 pt-28 md:pt-32 pb-24">
      <div className="max-w-6xl mx-auto">
        <p className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Masaya Hospitality · Monthly Stay</p>
        <h1 className="mt-4 mb-8 text-4xl sm:text-5xl tracking-tighter font-light text-[#1C1917]">Your reservation</h1>
        {children}
      </div>
    </section>
  );
}

function Banner({ icon: Icon, children, testid }) {
  return (
    <div data-testid={testid} className="mb-6 rounded-xl bg-[#F5F5F4] p-4 text-sm text-[#44403C] flex gap-2 items-start">
      <Icon className="w-4 h-4 mt-0.5 text-[#8A9A86] shrink-0" /> {children}
    </div>
  );
}

const TAX_STATUS = { pending_verification: "Pending verification", exempt_verified: "Exempt (verified)", taxable: "Taxes apply" };

function Summary({ r, bd, taxStatus }) {
  const rows = [["Guest", r.guest_name], ["Room", bd?.room_name], ["Check-in", r.check_in], ["Check-out", r.check_out], ["Nights", bd?.nights], ["Status", r.status],
    ["Tax status", TAX_STATUS[taxStatus] || "—"], ["Texas / Occupancy Tax", bd ? `${money(bd.texas_tax)} / ${money(bd.occupancy_tax)}` : "—"]];
  return (
    <div className={card} data-testid="pay-summary">
      <p className={eyebrow + " mb-4"}>Reservation summary</p>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        {rows.map(([k, v]) => (
          <div key={k}><dt className="text-xs text-[#78716C]">{k}</dt><dd data-testid={`pay-summary-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-[#1C1917]">{v ?? "—"}</dd></div>
        ))}
      </dl>
    </div>
  );
}

function StepHead({ n, icon: Icon, title, done }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${done ? "bg-[#8A9A86] text-white" : "bg-[#F5F5F4] text-[#44403C]"}`}>
        {done ? <CheckCircle2 className="w-4 h-4" /> : n}
      </span>
      <Icon className="w-4 h-4 text-[#8A9A86]" strokeWidth={1.75} />
      <p className="text-[#1C1917] font-medium text-sm">{title}</p>
    </div>
  );
}

function AgreementStep({ id, agreement, preview, revised, onDone }) {
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const sign = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/pay/${id}/agreement`, { full_name: name });
      toast.success("Agreement signed");
      onDone();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not sign the agreement. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className={card} data-testid="pay-step-agreement">
      <StepHead n={1} icon={FileSignature} title="Sign monthly stay agreement" done={Boolean(agreement)} />
      {agreement ? (
        <p data-testid="agreement-signed" className="text-sm text-[#44403C]">{agreement.title || "Agreement"} v{agreement.version} signed by {agreement.signed_full_name} on {new Date(agreement.signed_at).toLocaleString()}</p>
      ) : !preview?.available ? (
        <p data-testid="agreement-pending" className="text-sm text-[#44403C] font-light">Your monthly stay agreement is being prepared by Masaya. You'll be able to review and sign it here shortly.</p>
      ) : (
        <form onSubmit={sign} className="space-y-3">
          <pre data-testid="agreement-text" className="max-h-72 overflow-y-auto whitespace-pre-wrap font-sans text-xs leading-relaxed text-[#44403C] rounded-lg bg-[#FAFAF9] border border-[#E7E5E4] p-3">{preview.text}</pre>
          <input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} placeholder="Full legal name" data-testid="agreement-name" className={inp} />
          <label className="flex items-start gap-2 text-sm text-[#44403C]">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="agreement-checkbox" className="mt-1" />
            I have read and agree to this monthly stay agreement, including my written commitment to stay at least 30 consecutive nights{revised ? " and the revised total including Texas Tax and Occupancy Tax" : ""}.
          </label>
          <button disabled={!agree || busy || name.trim().length < 2} data-testid="agreement-submit" className={btn + " w-full"}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign agreement"}
          </button>
        </form>
      )}
    </div>
  );
}

function RevisedTotalStep({ id, bd, preview, onDone }) {
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const accept = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/pay/${id}/accept-revised-total`, { full_name: name, accepted_grand_total: bd?.grand_total });
      toast.success("Revised total accepted");
      onDone();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not record your acceptance. Please try again.");
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <div className={card + " border-[#FDE68A]"} data-testid="pay-step-revised-total">
      <StepHead n="1b" icon={FileSignature} title="Review and accept your revised total" done={false} />
      <p className="text-sm text-[#44403C]">
        Masaya reviewed your stay and the permanent-resident exemption does not apply. Texas Tax ({money(bd?.texas_tax)}) and Occupancy Tax ({money(bd?.occupancy_tax)}) have been added.
      </p>
      <div className="mt-3 rounded-lg bg-[#F5F5F4] p-3 text-sm flex justify-between">
        <span className="text-[#44403C]">Revised total</span><span data-testid="revised-total-amount" className="text-[#1C1917] font-medium">{money(bd?.grand_total)}</span>
      </div>
      <form onSubmit={accept} className="space-y-3 mt-3">
        <pre data-testid="revised-agreement-text" className="max-h-60 overflow-y-auto whitespace-pre-wrap font-sans text-xs leading-relaxed text-[#44403C] rounded-lg bg-[#FAFAF9] border border-[#E7E5E4] p-3">{preview.text}</pre>
        <input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} placeholder="Full legal name" data-testid="revised-name" className={inp} />
        <label className="flex items-start gap-2 text-sm text-[#44403C]">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="revised-checkbox" className="mt-1" />
          I accept the revised total of {money(bd?.grand_total)} and re-sign the monthly stay agreement above.
        </label>
        <button disabled={!agree || busy || name.trim().length < 2} data-testid="revised-accept-btn" className={btn + " w-full"}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "I accept the revised total"}
        </button>
      </form>
    </div>
  );
}

function IdStep({ id }) {
  const [info, setInfo] = useState(null);
  const [docType, setDocType] = useState("drivers_license");
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeNote, setStripeNote] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/pay/${id}/id-documents`)
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo({ status: "Not Started", verified: false, can_upload: true, uploads: [] }));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const raw = info?.status || "Not Started";
  const verified = Boolean(info?.verified);
  const pending = raw === "Pending Review" || raw === "Pending";
  const needsResubmit = raw === "Needs Resubmission" || raw === "Rejected" || raw === "Failed";
  const label = { Verified: "Verified", Approved: "Verified (ID reviewed by Masaya)", "Pending Review": "Pending Masaya review", Pending: "Pending Masaya review", "Needs Resubmission": "Needs resubmission", Rejected: "Not approved — please resubmit", Failed: "Not approved — please resubmit", "Not Started": "Not started" }[raw] || raw;
  const current = (info?.uploads || []).filter((u) => !u.superseded);

  const submit = async (e) => {
    e.preventDefault();
    if (!front && !back) { toast.error("Please add at least the front of your ID (a clear photo or PDF)."); return; }
    const tooBig = [front, back].find((f) => f && f.size > 10 * 1024 * 1024);
    if (tooBig) { toast.error("Each file must be 10 MB or smaller. Please choose a smaller photo."); return; }
    setBusy(true);
    try {
      for (const [side, file] of [["front", front], ["back", back]]) {
        if (!file) continue;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("document_type", docType);
        fd.append("side", side);
        await axios.post(`${API}/pay/${id}/id-documents`, fd);
      }
      toast.success("Your ID was uploaded securely and is now pending Masaya review.");
      setFront(null); setBack(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "We couldn't upload that file. Please use a JPEG, PNG, WEBP or PDF under 10 MB and try again.");
    } finally { setBusy(false); }
  };

  const startStripe = async () => {
    setStripeBusy(true); setStripeNote("");
    try {
      const { data } = await axios.post(`${API}/pay/${id}/id-verify`, { origin_url: window.location.origin });
      if (data?.session_url) window.location.href = data.session_url;
      else setStripeNote("Instant verification isn't available right now — please use secure upload above.");
    } catch (err) {
      setStripeNote("Instant verification isn't available right now — please use secure upload above.");
    } finally { setStripeBusy(false); }
  };

  const fileInputCls = "mt-1 w-full text-xs text-[#44403C] file:mr-3 file:rounded-full file:border-0 file:bg-[#1C1917] file:text-white file:px-3 file:py-2 file:text-xs";

  return (
    <div className={card} data-testid="pay-step-id">
      <StepHead n={2} icon={IdCard} title="Verify your government ID" done={verified} />
      <p className="text-sm text-[#44403C]">Status: <span data-testid="idv-status" className="text-[#1C1917]">{label}</span></p>
      {verified ? (
        <p className="mt-1 text-xs text-[#78716C]">Your identity is verified. No further action is needed.</p>
      ) : pending ? (
        <>
          <p className="mt-2 text-xs text-[#78716C]" data-testid="idv-pending">Your ID is securely submitted and awaiting Masaya review. We'll email you once it's approved. Your ID images are encrypted, private, and never shown publicly.</p>
          {current.length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="idv-uploaded-list">
              {current.map((u) => (
                <li key={u.id} data-testid={`idv-uploaded-${u.side}`} className="text-[11px] text-[#4A6A48] flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {u.side_label} · {u.document_label} · {u.status}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          <p className="mt-1 text-xs text-[#78716C]">Securely upload a clear photo of your government ID — from your phone camera or photo library. Your images are encrypted, private, reviewed only by authorized Masaya staff, and never shown publicly.</p>
          {needsResubmit && info?.review_note && (
            <p className="mt-2 text-xs text-[#92400E]" data-testid="idv-review-note">Masaya note: {info.review_note}</p>
          )}
          <form onSubmit={submit} className="mt-3 rounded-xl bg-[#FAFAF9] border border-[#E7E5E4] p-3 space-y-3" data-testid="idv-upload-form">
            <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="idv-doc-type" className={inp}>
              <option value="drivers_license">Driver's license</option>
              <option value="state_id">State ID</option>
              <option value="passport">Passport</option>
            </select>
            <label className="block" data-testid="idv-front-field">
              <span className="text-xs text-[#44403C]">Front of ID (required)</span>
              <input type="file" accept="image/*,application/pdf" onChange={(e) => setFront(e.target.files?.[0] || null)} data-testid="idv-front-input" className={fileInputCls} />
              {front && <span className="mt-1 block text-[11px] text-[#4A6A48]" data-testid="idv-front-chosen">Selected: {front.name}</span>}
            </label>
            <label className="block" data-testid="idv-back-field">
              <span className="text-xs text-[#44403C]">Back of ID (recommended)</span>
              <input type="file" accept="image/*,application/pdf" onChange={(e) => setBack(e.target.files?.[0] || null)} data-testid="idv-back-input" className={fileInputCls} />
              {back && <span className="mt-1 block text-[11px] text-[#4A6A48]" data-testid="idv-back-chosen">Selected: {back.name}</span>}
            </label>
            <p className="text-[11px] text-[#78716C]">Accepted: JPEG, PNG, WEBP or PDF, up to 10 MB each.</p>
            <button type="submit" disabled={busy} data-testid="idv-upload-submit" className={btn + " w-full"}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload ID securely"}
            </button>
          </form>
          <div className="mt-3">
            <button onClick={startStripe} disabled={stripeBusy} data-testid="idv-start" className="text-xs text-[#1C1917] underline underline-offset-2 disabled:opacity-60">
              {stripeBusy ? "Starting instant verification…" : "Prefer instant verification? Try Stripe Identity"}
            </button>
            {stripeNote && <p className="mt-1 text-[11px] text-[#92400E]" data-testid="idv-stripe-note">{stripeNote}</p>}
          </div>
        </>
      )}
    </div>
  );
}

function PayStep({ next, signed, taxStatus, revised, deadline, notice }) {
  const url = next?.checkout_url;
  const waiting = !signed ? "Please sign the monthly stay agreement first."
    : revised ? "Please accept the revised total above. Your payment link will follow."
    : taxStatus === "pending_verification" ? "Masaya is verifying your tax exemption eligibility. Your secure payment link will appear here once verified."
    : null;
  if (notice) {
    return (
      <div className={card} data-testid="pay-step-payment">
        <StepHead n={3} icon={CreditCard} title="Payment" done={false} />
        <p data-testid="pay-disabled-notice" className="text-sm text-[#44403C] font-light">{notice}. No payment can be made from this page.</p>
      </div>
    );
  }
  return (
    <div className={card} data-testid="pay-step-payment">
      <StepHead n={3} icon={CreditCard} title={next?.installment > 1 ? `Pay ${next.label}` : "Pay amount due today"} done={false} />
      {!url ? (
        <p data-testid="pay-not-ready" className="text-sm text-[#44403C] font-light">
          {waiting && next?.installment === 1 ? waiting : next?.installment > 1 ? `Your ${next.label} payment link will be sent on ${next.due_date}.` : "Your secure payment link will appear here once our team confirms availability."}
        </p>
      ) : !signed ? (
        <p data-testid="pay-sign-first" className="text-sm text-[#44403C] font-light">Please sign the monthly stay agreement to continue to payment.</p>
      ) : (
        <>
          <a href={url} data-testid="pay-checkout-btn" className={btn + " w-full"}>Pay {money(next.amount + (next.security_deposit || 0))}</a>
          {next.installment === 1 && deadline && <p className="mt-2 text-xs text-[#78716C]">Hold expires {new Date(deadline).toLocaleString()}.</p>}
        </>
      )}
    </div>
  );
}
